// CANONICAL COPY (GH #7115). src/utils/resolveReaders.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

const {
  realEmployees,
  isSampleEmployee,
  stripSeededEvidence,
  excludedSampleIds,
} = require('../sampleRecords.cjs');

/**
 * The one list of who must read a document (DOC-B-026, docs/document-readers-design.md).
 *
 * A document's required readers are a union of TARGETS, stored on the document
 * record as `doc.readerTargets`:
 *
 *   { id, type, value, deadline, note, assignedBy, assignedAt, source }
 *
 *   type            matches an (active) employee when
 *   all-employees   always
 *   employee        employee.id === value
 *   department      employee.department, trimmed and case-insensitive
 *   team            employee.team === value
 *   role            employee.assignedRoles[] has name === value, not unassigned
 *   jobTitle        employee.jobTitle, trimmed and case-insensitive
 *   user            read-only legacy: resolves to `value` as-is (a tenant user id)
 *
 * Documents written before the list existed carry `readerRequirement` /
 * `requiredReaders` instead. readerTargetsOf() normalises those at read time,
 * writing nothing, so every existing document resolves exactly as it did.
 */

/** Active = eligible for policy ack: not departed and not offboarding. */
function isActiveEmployee(e) {
  return !!e && e.status !== 'departed' && e.status !== 'offboarding';
}

/**
 * The active employees, without sample people (GH #7870) — an invented person
 * is never part of the population that owes a read.
 */
function activeEmployees(employees) {
  return realEmployees(employees).filter(isActiveEmployee);
}

const READER_TARGET_TYPES = ['all-employees', 'employee', 'department', 'team', 'role', 'jobTitle', 'user'];

// Target types whose value compares trimmed and case-insensitive.
const FOLDED_TARGET_TYPES = new Set(['department', 'jobTitle']);

/** The comparison form of a target value for its type. */
function normaliseTargetValue(type, value) {
  if (type === 'all-employees') return '';
  const s = value == null ? '' : String(value);
  return FOLDED_TARGET_TYPES.has(type) ? s.trim().toLowerCase() : s;
}

/** Two targets with the same key name the same population. */
function readerTargetKey(target) {
  return `${target.type}\u0000${normaliseTargetValue(target.type, target.value)}`;
}

// Legacy-derived targets get an id computed from their key, so the id a client
// reads from an unmaterialised document is the id the row keeps once the first
// readers write materialises the list (shared/documents/readers.js).
function legacyTargetId(type, value) {
  return `rt-legacy-${type}${type === 'all-employees' ? '' : `-${normaliseTargetValue(type, value)}`}`;
}

function legacyTarget(type, value) {
  return {
    id: legacyTargetId(type, value),
    type,
    value: type === 'all-employees' ? null : value,
    deadline: null,
    note: '',
    assignedBy: null,
    assignedAt: null,
    source: 'legacy-readerRequirement',
  };
}

function legacyUserTargets(doc) {
  const ids = Array.isArray(doc.requiredReaders) ? doc.requiredReaders : [];
  return ids.map((id) => legacyTarget('user', id));
}

/**
 * The document's reader targets. Returns `doc.readerTargets` when the document
 * carries the list; otherwise derives it from the legacy fields (design §a.2).
 *
 * @param {object} doc
 * @returns {object[]}
 */
function readerTargetsOf(doc) {
  if (!doc) return [];
  if (Array.isArray(doc.readerTargets)) return doc.readerTargets;
  // Two legacy values differing only in case or spacing ("Finance", " finance")
  // name one population and would share one id; keep the first.
  const seen = new Set();
  return legacyTargetsOf(doc).filter((t) => {
    const key = readerTargetKey(t);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function legacyTargetsOf(doc) {
  const req = doc.readerRequirement;
  if (!req) return legacyUserTargets(doc);
  switch (req.mode) {
    case 'none':
      return [];
    case 'all-employees':
      return [legacyTarget('all-employees', null)];
    case 'departments':
      return (req.departments || []).map((d) => legacyTarget('department', d));
    case 'roles':
      // `roles` always meant the employee's job title.
      return (req.roles || []).map((r) => legacyTarget('jobTitle', r));
    case 'specific':
      return (req.employees || []).map((id) => legacyTarget('employee', id));
    default:
      // Unknown mode — fall back to legacy
      return legacyUserTargets(doc);
  }
}

// ── The read-time fold of state.documentAssignments (design §d.1) ─────────
//
// TEMPORARY, said plainly: until RDRA-04's migration has run on every
// environment's tenants, some documents' readers still live only in the old
// `state.documentAssignments` list. The fold merges that list into the
// document's own targets at read time, so the portal, the emails and the
// compliance evidence all see one list. It is removed in the PR that records
// the migration's apply run; until then every resolver call passes the
// tenant's assignments explicitly — `[]` when it genuinely has none.

const LEGACY_ASSIGNMENT_TYPES = new Set(['employee', 'department', 'team', 'role']);

function inTestRun() {
  return typeof process !== 'undefined' && !!process.env &&
    (process.env.NODE_ENV === 'test' || !!process.env.VITEST || !!process.env.JEST_WORKER_ID);
}

let warnedMissingAssignments = false;

/**
 * The assignments argument, which is required while the fold exists. A call
 * site that forgets it would make its surface disagree with every other one,
 * so in tests it throws; in production it logs once and folds nothing — the
 * resolution every surface gave before the fold.
 */
function assignmentsArg(documentAssignments) {
  if (documentAssignments === undefined) {
    if (inTestRun()) {
      throw new Error('resolveReaders: documentAssignments is required while the legacy fold exists (pass [] for none)');
    }
    if (!warnedMissingAssignments) {
      warnedMissingAssignments = true;
      // eslint-disable-next-line no-console
      console.error('resolveReaders: called without documentAssignments — legacy assignments not folded');
    }
    return [];
  }
  return Array.isArray(documentAssignments) ? documentAssignments : [];
}

/** The earlier of two deadlines; null only when both are. */
function earlierDeadline(a, b) {
  if (!a) return b || null;
  if (!b) return a;
  return Date.parse(b) < Date.parse(a) ? b : a;
}

function assignmentTarget(a) {
  const type = a.target.type;
  const value = String(a.target.value);
  return {
    id: a.id || `da-legacy-${type}-${normaliseTargetValue(type, value)}`,
    type,
    value,
    deadline: a.deadline || null,
    note: a.note || '',
    assignedBy: a.assignedBy || null,
    assignedAt: a.assignedAt || null,
    source: 'legacy-assignment',
  };
}

/**
 * The document's targets with its active legacy assignments folded in. An
 * assignment naming a population the document already targets merges into
 * that target the way add-readers would — the earlier deadline wins, and a
 * note fills an empty one — so its deadline still reaches the portal. A
 * document whose list was written after the fold (`legacyAssignmentsFolded`)
 * already carries them and is returned as stored.
 *
 * @param {object} doc
 * @param {object[]} documentAssignments - state.documentAssignments
 * @returns {object[]}
 */
function foldLegacyAssignments(doc, documentAssignments) {
  const assignments = assignmentsArg(documentAssignments);
  const base = readerTargetsOf(doc);
  if (!doc || doc.legacyAssignmentsFolded === true) return base;
  const mine = assignments.filter((a) =>
    a && a.active && a.documentId === doc.id && a.target &&
    LEGACY_ASSIGNMENT_TYPES.has(a.target.type) &&
    a.target.value != null && String(a.target.value).trim() !== ''
  );
  if (mine.length === 0) return base;
  const out = base.map((t) => ({ ...t }));
  const byKey = new Map(out.map((t) => [readerTargetKey(t), t]));
  for (const a of mine) {
    const t = assignmentTarget(a);
    const existing = byKey.get(readerTargetKey(t));
    if (existing) {
      existing.deadline = earlierDeadline(existing.deadline || null, t.deadline);
      if (!existing.note && t.note) existing.note = t.note;
      continue;
    }
    out.push(t);
    byKey.set(readerTargetKey(t), t);
  }
  return out;
}

function hasActiveRole(employee, name) {
  return (employee.assignedRoles || []).some((r) => r && r.name === name && !r.unassignedAt);
}

/** The reader ids one target makes required, in employee-list order. */
function targetReaderIds(target, employees, active, removedSamples) {
  const type = target && target.type;
  switch (type) {
    case 'user':
      return target.value == null ? [] : [target.value];
    case 'all-employees':
      return active.map((e) => e.id);
    case 'employee':
      // A sample person named here stays required on purpose (GH #7870): the
      // document still owes a real reader, and readerAcknowledgement() never
      // credits a sample, so the requirement reads as unmet instead of vanishing.
      return removedSamples.has(target.value) || active.some((e) => e.id === target.value)
        ? [target.value]
        : [];
    case 'department':
    case 'jobTitle': {
      const field = type === 'department' ? 'department' : 'jobTitle';
      const wanted = normaliseTargetValue(type, target.value);
      if (!wanted) return [];
      return active
        .filter((e) => typeof e[field] === 'string' && e[field].trim().toLowerCase() === wanted)
        .map((e) => e.id);
    }
    case 'team':
      return active.filter((e) => e.team != null && e.team === target.value).map((e) => e.id);
    case 'role':
      return active.filter((e) => hasActiveRole(e, target.value)).map((e) => e.id);
    default:
      return [];
  }
}

/**
 * Every required reader of a document, with the targets that make each one
 * required. Insertion order is first-match order over the targets.
 *
 * @param {object} doc
 * @param {Array}  employees - state.employees
 * @param {Array}  documentAssignments - state.documentAssignments (required while the fold exists)
 * @returns {Map<string, object[]>} readerId → matching targets
 */
/** The employee population every target resolves against. */
function readerPopulation(employees) {
  const list = Array.isArray(employees) ? employees : [];
  const real = realEmployees(list);
  const removedSamples = new Set([
    ...excludedSampleIds(real),
    ...list.filter((e) => isSampleEmployee(e)).map((e) => e.id),
  ]);
  return { list, active: real.filter(isActiveEmployee), removedSamples };
}

function resolveReaderTargets(doc, employees = [], documentAssignments) {
  const targets = foldLegacyAssignments(doc, documentAssignments);
  const { list, active, removedSamples } = readerPopulation(employees);
  const out = new Map();
  for (const target of targets) {
    for (const id of targetReaderIds(target, list, active, removedSamples)) {
      if (!out.has(id)) out.set(id, []);
      out.get(id).push(target);
    }
  }
  return out;
}

/**
 * The targets of a document that resolve to nobody — a misspelt or emptied
 * department, a role nobody holds, `all-employees` with no active people, a
 * named employee id that matches no record, an unknown target type. Gate 3
 * fails on any of them rather than passing a document whose readers vanished
 * (fail-safe, §5). The one exemption is a named employee who exists but has
 * left: their leaving ends their own requirement.
 *
 * @returns {object[]} the dead targets, in target order
 */
function unresolvedReaderTargets(doc, employees = [], documentAssignments) {
  const { list, active, removedSamples } = readerPopulation(employees);
  return foldLegacyAssignments(doc, documentAssignments).filter((t) => {
    if (!t || targetReaderIds(t, list, active, removedSamples).length > 0) return false;
    if (t.type === 'employee' && list.some((e) => e && e.id === t.value)) return false;
    return true;
  });
}

/**
 * Resolve a document's required readers to a list of identifiers.
 *
 * @param {object} doc - Document object
 * @param {Array}  employees - Full employee list from state.employees
 * @param {Array}  documentAssignments - state.documentAssignments (required while the fold exists)
 * @returns {string[]} Array of employee IDs (or legacy user IDs for backwards compat)
 */
function resolveReaders(doc, employees = [], documentAssignments) {
  return [...resolveReaderTargets(doc, employees, documentAssignments).keys()];
}

/**
 * The one acknowledgement version rule (design §b): an acknowledgement counts
 * only when the document is published and the acknowledgement was recorded
 * against the version in force. Returns null for any other document, and null
 * matches nothing — so a draft's number can never be acknowledged into
 * evidence. Never `materialVersion`: the server sets that only at first
 * publication, so `materialVersion || version` names the first-published text
 * for good.
 *
 * @param {object} doc
 * @returns {string|null}
 */
function requiredAckVersion(doc) {
  if (!doc || doc.documentStatus !== 'published') return null;
  return doc.version || null;
}

/**
 * The acknowledgement versions that satisfy the version in force: that version
 * itself, plus every earlier version whose acknowledgements the person who
 * re-published chose to carry forward to it (`doc.ackCarryForwards`, written
 * only by documents-workflow publish with requireReread: false — founder
 * decision, design §b). Carry-forwards chain: v1 → v2 → v3 lets a read of v1
 * satisfy v3, and a re-publication that asked readers to re-read breaks the
 * chain, because no carry-forward names its version.
 *
 * Returns a Map version → the carry-forward entries leading from it to the
 * version in force ([] for the version in force itself). Empty when nothing
 * can be acknowledged.
 */
function acceptedAckVersions(doc) {
  const required = requiredAckVersion(doc);
  const out = new Map();
  if (!required) return out;
  out.set(required, []);
  const carries = Array.isArray(doc.ackCarryForwards) ? doc.ackCarryForwards : [];
  let grew = true;
  while (grew) {
    grew = false;
    for (const c of carries) {
      if (!c || !c.fromVersion || out.has(c.fromVersion) || !out.has(c.toVersion)) continue;
      out.set(c.fromVersion, [c, ...out.get(c.toVersion)]);
      grew = true;
    }
  }
  return out;
}

/**
 * The acknowledgement that satisfies the version in force for one reader, or
 * null. Evidence must say what was actually read: `version` is the version the
 * person acknowledged, and `carriedForward` lists the re-publications that
 * carried it to the version in force (empty for a read of that version). A
 * carried acknowledgement is never presented as a read of the newer text.
 *
 * Employee lookup wins when the id matches an employee (the portal store);
 * otherwise the legacy user store `doc.readAcknowledgements` is consulted.
 *
 * `method` says who recorded it (ackMethod, design §b): a person's own read
 * ('self'), a read an editor recorded for them ('on-behalf', with `onBehalf`
 * naming who, why and on what evidence), or a legacy record that names
 * neither ('unknown'). All three count; none is ever labelled as another.
 * Among the acknowledgements of the closest version, a self-read is
 * preferred, then one recorded on behalf (attributed), then an unknown one.
 *
 * @returns {{ version: string, at: string|null, carriedForward: object[], ack: object,
 *             method: 'self'|'on-behalf'|'unknown', onBehalf: object|null } | null}
 */
function readerAcknowledgement(readerId, doc, employees = []) {
  const accepted = acceptedAckVersions(doc);
  if (accepted.size === 0) return null;
  const found = (employees || []).find((e) => e.id === readerId);
  // A sample person never acknowledges anything, and a counted seed's
  // invented acknowledgements are stripped (GH #7870).
  if (found ? isSampleEmployee(found) : excludedSampleIds(employees).has(readerId)) return null;
  const emp = found ? stripSeededEvidence(found) : null;
  const acks = emp
    ? (emp.documentAcknowledgements || []).filter((a) => a && a.documentId === doc.id)
    : (doc.readAcknowledgements || []).filter((a) => a && a.user === readerId);
  // Prefer a read of the version in force, then the most recent carried one;
  // at the same distance, the person's own read over any other.
  let best = null;
  for (const a of acks) {
    if (!accepted.has(a.version)) continue;
    const chain = accepted.get(a.version);
    // The legacy user store holds only the user's own in-app read
    // (DocumentReviewModal stamps `user` with the signed-in user).
    const method = emp ? ackMethod(a, emp) : 'self';
    const better =
      !best ||
      chain.length < best.carriedForward.length ||
      (chain.length === best.carriedForward.length && METHOD_RANK[method] < METHOD_RANK[best.method]);
    if (better) {
      best = {
        version: a.version,
        at: a.acknowledgedAt || a.readAt || null,
        carriedForward: chain,
        ack: a,
        method,
        onBehalf: method === 'on-behalf' ? onBehalfOf(a) : null,
      };
    }
  }
  return best;
}

const METHOD_RANK = { self: 0, 'on-behalf': 1, unknown: 2 };

/**
 * How an employee acknowledgement was recorded (design §b, RDRC-04A):
 *   'self'      — the person's own read: the portal's `selfAcknowledged`
 *                 (employee-portal-update also stamps `recordedBy` with the
 *                 employee's id), or a record stamped by the employee's own
 *                 id or linked user.
 *   'on-behalf' — `method: 'on-behalf'` (employees-write
 *                 record-acknowledgement, server-stamped), or a legacy admin
 *                 record whose `recordedBy` is somebody else.
 *   'unknown'   — a legacy record naming no recorder at all. It still counts
 *                 (it predates the distinction and is not invented), but it is
 *                 never presented as the person's own read.
 *
 * @param {object} ack
 * @param {object} employee
 * @returns {'self'|'on-behalf'|'unknown'}
 */
function ackMethod(ack, employee) {
  if (!ack) return 'unknown';
  if (ack.method === 'on-behalf') return 'on-behalf';
  if (ack.selfAcknowledged === true) return 'self';
  const by = ack.recordedBy;
  if (!by) return 'unknown';
  if (employee && (by === employee.id || (employee.linkedUserId && by === employee.linkedUserId))) return 'self';
  return 'on-behalf';
}

/** Who recorded an on-behalf read, why, and on what evidence (nulls on legacy records). */
function onBehalfOf(ack) {
  return {
    recordedBy: ack.recordedBy || null,
    recordedByName: ack.recordedByName || null,
    reason: ack.reason || null,
    evidenceRef: ack.evidenceRef || null,
    recordedAt: ack.recordedAt || ack.acknowledgedAt || null,
  };
}

/**
 * Has a resolved reader acknowledged the version of a document in force —
 * directly, or through a carry-forward the re-publisher chose?
 *
 * @param {string} readerId  - employee ID or legacy user ID
 * @param {object} doc       - document object
 * @param {Array}  employees - state.employees
 * @returns {boolean}
 */
function hasReaderAcknowledged(readerId, doc, employees = []) {
  return readerAcknowledgement(readerId, doc, employees) !== null;
}

/**
 * Check whether a document has any reader requirement at all.
 * Used by UI components to decide whether to show ack-related elements.
 *
 * @param {object} doc - Document object
 * @param {Array}  employees - Employee list
 * @returns {boolean}
 */
function hasReaderRequirement(doc, employees = [], documentAssignments) {
  return resolveReaders(doc, employees, documentAssignments).length > 0;
}

module.exports = {
  READER_TARGET_TYPES,
  isActiveEmployee,
  normaliseTargetValue,
  readerTargetKey,
  readerTargetsOf,
  foldLegacyAssignments,
  earlierDeadline,
  resolveReaderTargets,
  resolveReaders,
  unresolvedReaderTargets,
  requiredAckVersion,
  acceptedAckVersions,
  readerAcknowledgement,
  ackMethod,
  hasReaderAcknowledged,
  hasReaderRequirement,
};
